/**
 * ⚠️ COMPONENTE INACTIVO - NO SE USA EN LA UI ACTUAL
 *
 * Este componente existe pero no está siendo utilizado activamente en el sistema.
 * No hay forma de abrirlo desde la interfaz de usuario (no hay botón que lo active).
 *
 * Estado: Código mantenido para posible uso futuro, pero actualmente inactivo.
 *
 * TODO: Si se decide implementar la funcionalidad de cuotas:
 * 1. Agregar botón en OrderDetailsModal para abrir este modal
 * 2. Implementar la lógica de generación de cuotas
 * 3. Activar este componente
 */

import React from 'react';
import { DecimalNumberInput } from '../../../ui';
import { formatDecimal } from '../../../../utils/localeNumber';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Notifications as NotificationIcon,
} from '@mui/icons-material';
import { SalesOrder } from '../../../../types/sales.types';
import { useInstallmentsModal } from './InstallmentsModal.hooks';
import { FREQUENCY_OPTIONS } from './InstallmentsModal.constants';
import * as S from './InstallmentsModal.styles';

interface InstallmentsModalProps {
  open: boolean;
  onClose: () => void;
  order: SalesOrder | null;
  onInstallmentsUpdated?: () => void;
}

const InstallmentsModal: React.FC<InstallmentsModalProps> = ({
  open,
  onClose,
  order,
  onInstallmentsUpdated,
}) => {
  const {
    installments,
    isLoading,
    error,
    success,
    showCreateForm,
    setShowCreateForm,
    newInstallmentData,
    pendingBalance,
    handleGenerateInstallments,
    handleMarkAsPaid,
    handleCancelInstallment,
    updateFormField,
    getStatusChip,
    formatCurrency,
    formatDate,
    getDaysUntilDue,
    getInstallmentsSummary,
  } = useInstallmentsModal({ open, order, onInstallmentsUpdated });

  if (!open || !order) return null;

  const summary = getInstallmentsSummary();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth {...S.StyledDialog}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">Plan de Cuotas</Typography>
            <Typography variant="h6" color="primary">
              {order.orderCode}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        {success && (
          <Box sx={{ p: 2 }}>
            <Alert severity="success">{success}</Alert>
          </Box>
        )}

        {isLoading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            {/* Order Summary */}
            <S.OrderSummaryBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" gutterBottom>
                    Información de la venta
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total de la venta:
                      </Typography>
                      <Typography variant="h6">{formatCurrency(order.totalAmount)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Saldo Pendiente:
                      </Typography>
                      <Typography variant="h6" color="error">
                        {formatCurrency(pendingBalance)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4}>
                  {installments.length > 0 && (
                    <Card variant="outlined">
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Resumen de Cuotas
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Total:</Typography>
                          <Typography variant="body2" fontWeight="600">
                            {summary.total}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Pagadas:</Typography>
                          <Typography variant="body2" color="success.main">
                            {summary.paid}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Pendientes:</Typography>
                          <Typography variant="body2" color="warning.main">
                            {summary.pending}
                          </Typography>
                        </Box>
                        {summary.overdue > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Vencidas:</Typography>
                            <Badge badgeContent={summary.overdue} color="error">
                              <Typography variant="body2" color="error.main">
                                {summary.overdue}
                              </Typography>
                            </Badge>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Grid>
              </Grid>
            </S.OrderSummaryBox>

            <Box sx={{ p: 3 }}>
              {/* Create New Installments */}
              {(installments.length === 0 || showCreateForm) && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <AddIcon color="primary" />
                        Generar Plan de Cuotas
                      </Typography>
                      {installments.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowCreateForm(false)}
                        >
                          Cancelar
                        </Button>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <DecimalNumberInput
                          fullWidth
                          label="Número de Cuotas"
                          value={newInstallmentData.numberOfInstallments}
                          onChange={(val) =>
                            updateFormField(
                              'numberOfInstallments',
                              val != null ? Math.trunc(val) : 1,
                            )
                          }
                          decimalPlaces={0}
                          min={1}
                          max={24}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Frecuencia</InputLabel>
                          <Select
                            value={newInstallmentData.frequency}
                            label="Frecuencia"
                            onChange={(e) => updateFormField('frequency', e.target.value)}
                          >
                            {FREQUENCY_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Primera Cuota"
                          type="date"
                          value={newInstallmentData.firstDueDate}
                          onChange={(e) => updateFormField('firstDueDate', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Monto por Cuota"
                          value={formatDecimal(newInstallmentData.installmentAmount, 2)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">Bs</InputAdornment>,
                            readOnly: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Notas del Plan"
                          multiline
                          rows={2}
                          value={newInstallmentData.notes}
                          onChange={(e) => updateFormField('notes', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          onClick={handleGenerateInstallments}
                          disabled={isLoading}
                          startIcon={<ScheduleIcon />}
                        >
                          Generar Plan de Cuotas
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Existing Installments */}
              {installments.length > 0 && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">Cuotas Programadas ({installments.length})</Typography>
                    {!showCreateForm && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setShowCreateForm(true)}
                        size="small"
                      >
                        Agregar Más Cuotas
                      </Button>
                    )}
                  </Box>

                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell>Cuota #</TableCell>
                          <TableCell>Fecha de Vencimiento</TableCell>
                          <TableCell align="right">Monto</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Días Restantes</TableCell>
                          <TableCell>Fecha de Pago</TableCell>
                          <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {installments.map((installment) => {
                          const daysUntilDue = getDaysUntilDue(installment.dueDate);
                          const statusConfig = getStatusChip(
                            installment.status,
                            installment.dueDate,
                          );
                          const StatusIcon = statusConfig.icon;

                          return (
                            <TableRow key={installment.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="600">
                                  Cuota {installment.installmentNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CalendarIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {formatDate(installment.dueDate)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="600">
                                  {formatCurrency(installment.amount)}
                                </Typography>
                                {installment.lateFee > 0 && (
                                  <Typography variant="caption" color="error" display="block">
                                    + {formatCurrency(installment.lateFee)} mora
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={<StatusIcon fontSize="small" />}
                                  label={statusConfig.label}
                                  color={statusConfig.color}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {installment.status === 'PAID' ? (
                                  <Typography variant="body2" color="success.main">
                                    Pagada
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color={
                                      daysUntilDue < 0
                                        ? 'error'
                                        : daysUntilDue <= 7
                                          ? 'warning.main'
                                          : 'text.primary'
                                    }
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                  >
                                    {daysUntilDue < 0 && <WarningIcon fontSize="small" />}
                                    {daysUntilDue < 0
                                      ? `${Math.abs(daysUntilDue)} días vencida`
                                      : daysUntilDue === 0
                                        ? 'Vence hoy'
                                        : `${daysUntilDue} días`}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {installment.paidDate ? (
                                  <Typography variant="body2" color="text.secondary">
                                    {formatDate(installment.paidDate)}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    Pendiente
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {installment.status === 'PENDING' && (
                                    <Tooltip title="Marcar como Pagada">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleMarkAsPaid(installment.id)}
                                        color="success"
                                      >
                                        <CheckIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}

                                  {installment.status !== 'PAID' &&
                                    installment.status !== 'CANCELLED' && (
                                      <Tooltip title="Cancelar Cuota">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleCancelInstallment(installment.id)}
                                          color="error"
                                        >
                                          <CancelIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Summary Card */}
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Resumen del Plan
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Total Cuotas:
                          </Typography>
                          <Typography variant="h6">{installments.length}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Monto Total:
                          </Typography>
                          <Typography variant="h6">
                            {formatCurrency(installments.reduce((sum, i) => sum + i.amount, 0))}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Pagado:
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {formatCurrency(
                              installments
                                .filter((i) => i.status === 'PAID')
                                .reduce((sum, i) => sum + i.amount, 0),
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Pendiente:
                          </Typography>
                          <Typography variant="h6" color="error">
                            {formatCurrency(
                              installments
                                .filter((i) => i.status === 'PENDING')
                                .reduce((sum, i) => sum + i.amount, 0),
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* No Installments Message */}
              {installments.length === 0 && !showCreateForm && (
                <S.EmptyStateBox>
                  <ScheduleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No hay cuotas programadas
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Genere un plan de cuotas para esta venta
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateForm(true)}
                  >
                    Generar Plan de Cuotas
                  </Button>
                </S.EmptyStateBox>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'grey.200' }}>
        <S.CloseButton onClick={onClose}>Cerrar</S.CloseButton>
        {installments.length > 0 && (
          <S.RemindersButton
            variant="outlined"
            startIcon={<NotificationIcon />}
            onClick={() => {
              alert('Funcionalidad de recordatorios en desarrollo');
            }}
          >
            Configurar Recordatorios
          </S.RemindersButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InstallmentsModal;
